import moment from "moment";
import { createSelector } from "reselect";
import { getRandomColor } from '../../helpers/utility';

const getDailyStatistics = state => state.SystemInfos.get('dailyStatistics');
const getWeeklyStatistics = state => state.SystemInfos.get('weeklyStatistics');
const getClinicsWithMostPatients = state => state.SystemInfos.get('clinicsWithMostPatients');
const getUsersWithMostPatients = state => state.SystemInfos.get('usersWithMostPatients');
const getBackupFilter = state => state.SystemInfos.get('backupFilter');
const getBackups = state => state.SystemInfos.get('backupsData');

const selectDailyStatisticsOfDayFromState = (state, dateAsMoment) => createSelector([getDailyStatistics], dailyStatistics => {
  return dailyStatistics.find(d => d.date === dateAsMoment.format('YYYY-MM-DD'));
})(state);

const getWeeklyStatisticsOfWeek = (state, weekNumber) => createSelector([getWeeklyStatistics], weeklyStatistics => {
  return weeklyStatistics.find(d => d.weekNumber === weekNumber);
})(state);

const getSortedClinicsWithMostPatients = createSelector([getClinicsWithMostPatients], clinicsWithMostPatients => {
  return clinicsWithMostPatients.map(item => ({
    ...item,
    color: getRandomColor()
  }))
    .sort((a, b) => (a.patientsCount > b.patientsCount) ? -1 : 1);
});

const getSortedUsersWithMostPatients = createSelector([getUsersWithMostPatients], usesWithMostPatients => {
  return usesWithMostPatients.map(item => ({
    ...item,
    title: `${item.lastName} ${item.firstName}`,
    color: getRandomColor()
  })).sort((a, b) => (a.patientsCount > b.patientsCount) ? -1 : 1);
});

const getFilteredBackups = createSelector(
  [getBackupFilter, getBackups],
  (filter, backups) => {

    switch (filter) {
      case "all":
        return backups;

      case "personal":
        return backups.filter(record => record.resource.toLowerCase().includes("personal"));

      case "environment":
        return backups.filter(record => record.resource.toLowerCase().includes("environment"));

      case "other":
        return backups.filter(record => record.resource.toLowerCase().includes("other"));

      case "medical":
        return backups.filter(record => record.resource.toLowerCase().includes("medical"));

      case "dicomServer":
        return backups.filter(record => record.resource.toLowerCase().includes("dicom server"));

      case "dicomIndex":
        return backups.filter(record => record.resource.toLowerCase().includes("dicom db"));

      case "eprescription":
        return backups.filter(record => record.resource.toLowerCase().includes("eprescription"));

      case "mongo":
        return backups.filter(record => record.resource.toLowerCase().includes("mongdb"));

      default:
        return backups;
    }

  });

function transformDailyStatistics(dailyStatistics, startDate, endDate) {
  let tmp = {}
  let return_value = [];

  Object.entries(dailyStatistics).forEach(([date, values], index) => {
    Object.keys(values).forEach(title => {
      if (!tmp[title]) tmp[title] = {};
      tmp[title][date] = values[title].value
      tmp[title]['id'] = values[title].id
    });
  }, []);

  Object.entries(tmp).forEach(([title, values]) => {
    const { id, ...restvalues } = values;

    // restvalues contains data for all days that user requested and is stored in reducer. 
    // We want to filter restvalues using the current date filters.
    const filteredRestValues = Object.keys(restvalues)
      .filter((key) => moment(key).isBetween(startDate, endDate, 'days', '[]'))
      .reduce((obj, key) => {
        return Object.assign(obj, {
          [key]: restvalues[key]
        });
      }, {});

    const total = Object.values(filteredRestValues).reduce((a, b) => a + b, 0);
    return_value.push({ ...values, title: title, total: total })
  })

  return return_value;
}

function sortDailyStatistics(dailyStatistics, orderByCatecgory, sortingOrder) {
  if (orderByCatecgory) {
    return dailyStatistics.sort((a, b) => sortingOrder === 'ascend' ?
      a[orderByCatecgory] - b[orderByCatecgory] :
      b[orderByCatecgory] - a[orderByCatecgory]
    )
  }
  return dailyStatistics;
}

const filterDailyStatisticsBasedOnCategory = (state, statisticCategory, orderByCatecgory, sortingOrder, startDate, endDate) => createSelector(
  [getDailyStatistics], dailyStatistics => {
    let calculated_data = {};

    const filsRelatedFilters = [
      "nonDicomCount",
      "nonDicomSize",
      "dicomStudiesCount",
      "dicomSeriesCount",
      "dicomSize"
    ];

    dailyStatistics.forEach(daily => {
      const { date, clinics } = daily;
      let tmp = {};
      clinics.forEach(clinic => {
        tmp[clinic.name] = {
          value: clinic.activityOfUsers.length ? 0 : null,
          id: clinic.id
        }

        clinic.activityOfUsers.forEach(user => {
          if (statisticCategory === "uniqueLogins") {
            tmp[clinic.name].value += (user.logIns && user.logIns.length) ? 1 : 0
          } else if (statisticCategory === "totalLogins") {
            tmp[clinic.name].value += (user.logIns && user.logIns.length) ? user.logIns.length : 0
          }
          else if (filsRelatedFilters.includes(statisticCategory)) {
            tmp[clinic.name].value += user.filesAdded[statisticCategory]
          }
          else {
            tmp[clinic.name].value += user[statisticCategory]
          }
        });
      });

      calculated_data[date] = tmp;
    })

    let return_value = transformDailyStatistics(calculated_data, startDate, endDate)
    return sortDailyStatistics(return_value, orderByCatecgory, sortingOrder);
  }
)(state);

const filterDailyStatisticsForClinic = (state, statisticCategory, orderByCatecgory, sortingOrder, clinicId, startDate, endDate) => createSelector(
  [getDailyStatistics], dailyStatistics => {
    let calculated_data = {};
    const filsRelatedFilters = [
      "nonDicomCount",
      "nonDicomSize",
      "dicomStudiesCount",
      "dicomSeriesCount",
      "dicomSize"
    ];

    dailyStatistics.forEach(daily => {
      const { date, clinics } = daily;
      let tmp = {};
      clinics.forEach(clinic => {
        if (clinic.id === clinicId) {
          clinic.activityOfUsers.forEach(user => {
            let value;

            if (statisticCategory === "uniqueLogins") {
              value = (user.logIns && user.logIns.length) ? 1 : 0
            } else if (statisticCategory === "totalLogins") {
              value = (user.logIns && user.logIns.length) ? user.logIns.length : 0
            }
            else if (filsRelatedFilters.includes(statisticCategory)) {
              value = user.filesAdded[statisticCategory]
            }
            else {
              value = user[statisticCategory]
            }

            tmp[user.name] = {
              value: value ?? null,
              id: user.id
            }
          });
        }
      });

      calculated_data[date] = tmp;
    });

    let return_value = transformDailyStatistics(calculated_data, startDate, endDate)
    return sortDailyStatistics(return_value, orderByCatecgory, sortingOrder);
  }
)(state);

export {
  getSortedClinicsWithMostPatients, getSortedUsersWithMostPatients, getFilteredBackups,
  selectDailyStatisticsOfDayFromState, getWeeklyStatisticsOfWeek,
  filterDailyStatisticsBasedOnCategory, filterDailyStatisticsForClinic
};
